<template>
  <div class="patient-credit-form">
    <div class="loading loading-lg" v-if="loading"></div>
    <div class="columns" v-else>
      <div class="column col-12" v-if="cashier">
        <div class="cash-flow-summary">
          Saldo atual do caixa: {{ cashier.balance | currency }}
        </div>
      </div>
      <div class="column col-4 col-md-6 col-sm-12 form-group"
           :class="{'has-error': $v.form.cashierId.$error}" v-if="cashiers">
        <label class="form-label">Caixa</label>
        <select class="form-select" v-model="form.cashierId"
                @blur="$v.form.cashierId.$touch()" @change="setDefaultCategory">
          <option value="">Selecione um caixa</option>
          <option v-for="(item, i) in cashiers" :value="item.id" :key="i">
            {{ item.bankAccount.name }}
          </option>
        </select>
        <template v-if="$v.form.cashierId.$error">
          <div class="form-input-hint" v-if="!$v.form.cashierId.required">Campo obrigatório</div>
        </template>
      </div>
      <div class="column col-4 col-md-6 col-sm-12 form-group"
           :class="{'has-error': $v.form.type.$error}">
        <label class="form-label">Operação</label>
        <select class="form-select" v-model="form.type" :disabled="!!fixedType"
                @blur="$v.form.type.$touch()" @change="onTypeChange">
          <option value="deposit">Depósito (entrada de crédito)</option>
          <option value="refund">Devolução (saída de crédito)</option>
        </select>
      </div>
      <div class="column col-4 col-md-12 col-sm-12 form-group"
           :class="{'has-error': $v.form.categoryId.$error}">
        <label class="form-label">Categoria</label>
        <select class="form-select" v-model="form.categoryId"
                @blur="$v.form.categoryId.$touch()">
          <option value="">Selecione uma categoria</option>
          <option v-for="(item, i) in filteredCategories" :value="item.id" :key="i">
            <template v-if="item.parent">{{ item.parent.name }} > </template>{{ item.name }}
          </option>
        </select>
        <template v-if="$v.form.categoryId.$error">
          <div class="form-input-hint" v-if="!$v.form.categoryId.required">Campo obrigatório</div>
        </template>
      </div>
      <div class="column col-8 col-md-12 col-sm-12 form-group"
           :class="{'has-error': $v.form.patient.id.$error}">
        <label class="form-label">Paciente</label>
        <dx-autocomplete
          id="patient-name"
          v-model="patient"
          :readonly="!!form.patient.id"
          :disabled="!!fixedPatient"
          :source="findPatient"
          label="name"
          track-by="id"
          @select="setPatient"
          :debounce="800"
          @blur="$v.form.patient.id.$touch()"
          placeholder="Nome do paciente, data de nascimento ou CPF"
        >
          <button slot="action" @click="unsetPatient" v-if="!fixedPatient"
                  class="btn btn-action input-group-btn btn-icon"
                  :class="patient ? 'btn-gray' : 'btn-neutral'"
                  tabindex="-1">
            <fa-icon :icon="['fal', patient ? 'times' : 'search']"></fa-icon>
          </button>
          <template v-slot="{ item }">
            <div>{{ item.name }}</div>
            <small class="text-primary" v-if="item.id === null">Paciente não encontrado</small>
          </template>
        </dx-autocomplete>
        <template v-if="$v.form.patient.id.$error">
          <div class="form-input-hint" v-if="!$v.form.patient.id.required">Campo obrigatório</div>
        </template>
      </div>
      <div class="column col-4 col-md-12 col-sm-12 form-group">
        <label class="form-label">Saldo em crédito</label>
        <input type="text" class="form-input text-right text-bold" readonly
               :class="{'text-error': balance < 0}"
               :value="balance | currency">
      </div>
      <div class="column col-5 col-md-6 col-sm-12 form-group"
           :class="{'has-error': $v.form.methodId.$error}">
        <label class="form-label">Forma de pagamento</label>
        <select class="form-select" v-model="form.methodId" @blur="$v.form.methodId.$touch()">
          <option value="">[Selecione]</option>
          <option v-for="item in filteredMethods" :value="item.id" :key="item.id">
            {{ item.name }}
          </option>
        </select>
        <template v-if="$v.form.methodId.$error">
          <div class="form-input-hint" v-if="!$v.form.methodId.required">Campo obrigatório</div>
        </template>
      </div>
      <div class="column col-4 col-md-6 col-sm-12 form-group"
           :class="{'has-error': $v.form.date.$error}">
        <label class="form-label">Data</label>
        <dx-input-date id="credit-date" class="form-input" v-model="form.date"
                       @blur="$v.form.date.$touch()" />
        <template v-if="$v.form.date.$error">
          <div class="form-input-hint" v-if="!$v.form.date.required">Campo obrigatório</div>
          <div class="form-input-hint" v-else-if="!$v.form.date.date">Data inválida</div>
        </template>
      </div>
      <div class="column col-3 col-md-12 col-sm-12 form-group"
           :class="{'has-error': $v.form.value.$error}">
        <label class="form-label">Valor</label>
        <dx-input-number id="credit-value" v-model="form.value" class="form-input text-right"
                         :precision="2" maxlength="10" @blur="$v.form.value.$touch()"/>
        <template v-if="$v.form.value.$error">
          <div class="form-input-hint" v-if="!$v.form.value.minValue">Campo obrigatório</div>
          <div class="form-input-hint" v-else-if="!$v.form.value.maxValue">
            Valor maior que o saldo em crédito
          </div>
        </template>
      </div>
      <div class="column col-12 form-group">
        <label for="credit-notes" class="form-label">Observação</label>
        <textarea id="credit-notes" class="form-input" v-model="form.notes" rows="2" />
      </div>
      <div class="column col-12" v-if="!hideButtons">
        <button class="btn btn-primary mr-1" @click="save" :disabled="saving"
                :class="{loading: saving}">
          Salvar
        </button>
        <button class="btn btn-gray" @click="clear" :disabled="saving">Limpar</button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { required, minValue, maxValue } from 'vuelidate/lib/validators';
import { date } from '@/data/validators';

export default {
  props: {
    cashiers: {
      type: Array,
      default: null,
    },
    fixedPatient: {
      type: Object,
      default: null,
    },
    fixedType: {
      type: String,
      default: '',
    },
    hideButtons: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      methods: [],
      categories: [],
      patient: null,
      balance: 0,
      form: this.blankForm(),
    };
  },
  computed: {
    cashier() {
      if (!this.cashiers || !this.form.cashierId) return null;
      return this.cashiers.find(({ id }) => id === this.form.cashierId) || null;
    },
    financialType() {
      return this.form.type === 'deposit' ? 'revenue' : 'expense';
    },
    filteredCategories() {
      const label = item => (item.parent ? `${item.parent.name} > ${item.name}` : item.name);
      return this.categories
        .filter(item => item.type === this.financialType)
        .sort((a, b) => label(a).localeCompare(label(b)));
    },
    filteredMethods() {
      return this.methods.filter(item => (
        ['all', this.financialType].includes(item.type)
        && item.documentType && item.documentType.type !== 'credit'
      ));
    },
  },
  validations() {
    const rules = {
      form: {
        type: { required },
        cashierId: {},
        categoryId: { required },
        methodId: { required },
        patient: {
          id: { required },
        },
        date: { required, date },
        value: {
          required,
          minValue: minValue(0.01),
          maxValue: maxValue(this.form.type === 'refund' ? this.balance : Number.MAX_SAFE_INTEGER),
        },
      },
    };
    if (this.cashiers) {
      rules.form.cashierId = { required };
    }
    return rules;
  },
  watch: {
    fixedPatient: {
      immediate: true,
      handler(value) {
        if (value && value.id) {
          this.setPatient(value);
        }
      },
    },
    cashiers(value) {
      if (value && value.length === 1 && !this.form.cashierId) {
        this.form.cashierId = value[0].id;
        this.setDefaultCategory();
      }
    },
  },
  async mounted() {
    this.loading = true;
    this.$emit('loading', true);
    if (this.fixedType) {
      this.form.type = this.fixedType;
    }
    await Promise.all([this.loadMethods(), this.loadCategories()]);
    if (this.cashiers && this.cashiers.length === 1) {
      this.form.cashierId = this.cashiers[0].id;
    }
    this.setDefaultCategory();
    this.loading = false;
    this.$emit('loading', false);
  },
  methods: {
    blankForm() {
      return {
        type: this.fixedType || 'deposit',
        cashierId: '',
        categoryId: '',
        methodId: '',
        patient: {
          id: '',
          name: '',
        },
        date: moment().format('YYYY-MM-DD'),
        value: 0,
        notes: '',
      };
    },
    loadMethods() {
      const params = { active: true, limit: 0 };
      return this.$http.get('/payment-methods', { params })
        .then(({ data }) => {
          this.methods = data.items;
        })
        .catch(() => {});
    },
    loadCategories() {
      const params = { active: true, limit: 0 };
      return this.$http.get('/categories', { params })
        .then(({ data }) => {
          this.categories = data.items || [];
        })
        .catch(() => {});
    },
    loadBalance() {
      if (!this.form.patient.id) {
        this.balance = 0;
        return Promise.resolve();
      }
      const params = { patientId: this.form.patient.id };
      return this.$http.get('/patient-credits/balance', { params })
        .then(({ data }) => {
          this.balance = data.balance || 0;
        })
        .catch(() => {
          this.balance = 0;
        });
    },
    findPatient(search) {
      return this.$http.get('/patients/search', { params: { search } })
        .then(({ data }) => {
          if (data.items.length === 0) {
            return [{ id: null, name: search }];
          }
          return data.items;
        })
        .catch(() => []);
    },
    setPatient(data) {
      if (data && data.id) {
        this.patient = { id: data.id, name: data.name };
        this.form.patient = { id: data.id, name: data.name };
        this.loadBalance();
      } else {
        this.unsetPatient();
      }
    },
    unsetPatient() {
      this.patient = null;
      this.form.patient = { id: '', name: '' };
      this.balance = 0;
    },
    onTypeChange() {
      this.form.methodId = '';
      this.setDefaultCategory();
    },
    setDefaultCategory() {
      const current = this.filteredCategories.find(({ id }) => id === this.form.categoryId);
      if (current) return;

      let categoryId = '';
      if (this.cashier && this.cashier.categories) {
        const category = this.cashier.categories[this.financialType];
        // eslint-disable-next-line no-underscore-dangle
        if (category && category._id) {
          // eslint-disable-next-line no-underscore-dangle
          categoryId = category._id;
        }
      }
      this.form.categoryId = categoryId;
    },
    save() {
      this.$v.form.$touch();
      if (this.$v.form.$error) {
        return Promise.resolve(null);
      }

      if (this.cashier && this.form.type === 'refund') {
        const method = this.methods.find(({ id }) => id === this.form.methodId);
        if (method && method.documentType.type === 'money'
          && this.cashier.balance < this.form.value) {
          this.$toast.show('Saldo do caixa insuficiente para a devolução', { type: 'error' });
          return Promise.resolve(null);
        }
      }

      this.saving = true;
      const data = {
        type: this.form.type,
        cashierId: this.form.cashierId || null,
        patientId: this.form.patient.id,
        methodId: this.form.methodId,
        categoryId: this.form.categoryId,
        date: this.form.date,
        value: this.form.value,
        notes: this.form.notes,
      };

      return this.$http.post('/patient-credits', data)
        .then(({ data: entry }) => {
          this.$toast.show(this.form.type === 'deposit'
            ? 'Depósito registrado com sucesso!'
            : 'Devolução registrada com sucesso!');
          this.$emit('saved', entry);
          this.clear(!!this.fixedPatient);
          return entry;
        })
        .catch((e) => {
          let message = 'Não foi possível realizar a operação solicitada';
          if (e.response && e.response.data && e.response.data.message) {
            ({ message } = e.response.data);
          }
          this.$toast.show(message, { type: 'error' });
          return null;
        })
        .finally(() => {
          this.saving = false;
        });
    },
    clear(keepPatient = false) {
      const { cashierId, type } = this.form;
      this.form = this.blankForm();
      this.form.type = type;
      this.form.cashierId = cashierId;
      if (keepPatient && this.fixedPatient) {
        this.setPatient(this.fixedPatient);
      } else {
        this.unsetPatient();
      }
      this.setDefaultCategory();
      this.$v.form.$reset();
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
.patient-credit-form {
  .cash-flow-summary {
    background-color: $gray-color-ultra-light;
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    margin: $layout-spacing-sm 0 $layout-spacing 0;
    padding: $layout-spacing;
  }
}
</style>
